import React, { useEffect, useState } from "react";
import {
  Result,
  Button,
  Card,
  Descriptions,
  Typography,
  Spin,
  Steps,
  Divider
} from "antd";
import {
  CheckCircleOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  HomeOutlined,
  MailOutlined,
  TruckOutlined,
  UserOutlined
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(10);

  // Función para asegurar que el total sea un número
  const ensureNumber = (value) => {
    if (value === undefined || value === null) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  // Función para formatear el precio de forma segura
  const formatPrice = (price) => {
    const num = ensureNumber(price);
    return num.toFixed(2);
  };

  useEffect(() => {
    // Obtener datos del localStorage como respaldo
    const pendingTotal = localStorage.getItem('pending_cart_total');
    const pendingCartId = localStorage.getItem('pending_cart_id');
    
    // Usar datos de location.state o del localStorage
    const demoOrder = {
      order_id: location.state?.orderId || pendingCartId || `ORD-${Date.now().toString().slice(-8)}`,
      order_number: `#${Math.floor(100000 + Math.random() * 900000)}`,
      date: new Date().toLocaleDateString('es-MX'),
      total: ensureNumber(location.state?.total || pendingTotal || 0),
      items: location.state?.itemsCount || 0,
      estimated_delivery: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('es-MX'),
      customer_name: "Juan Pérez",
      customer_email: "juan@ejemplo.com",
      shipping_address: "Av. Revolución 123, CDMX",
      products: location.state?.products || []
    };

    console.log("Datos del pedido recibidos:", {
      locationState: location.state,
      localStorageTotal: pendingTotal,
      parsedTotal: ensureNumber(location.state?.total || pendingTotal || 0)
    });

    setTimeout(() => {
      setOrderInfo(demoOrder);
      setLoading(false);
    }, 1500);

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Limpiar localStorage después de mostrar la confirmación
          localStorage.removeItem('pending_cart_total');
          localStorage.removeItem('pending_cart_id');
          navigate("/marketplace");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, location.state]);

  const renderProductSummary = () => {
    if (!orderInfo?.products || orderInfo.products.length === 0) {
      return (
        <div style={{ marginTop: 20 }}>
          <Title level={5}>Productos comprados:</Title>
          <Paragraph type="secondary">
            Información de productos no disponible
          </Paragraph>
        </div>
      );
    }

    return (
      <div style={{ marginTop: 20 }}>
        <Title level={5}>Productos comprados:</Title>
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {orderInfo.products.map((product, index) => (
            <li key={index} style={{ marginBottom: 8 }}>
              ✓ {product.name} - {product.quantity} x ${formatPrice(product.price)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '70vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 20 }}>Cargando confirmación de tu compra...</Title>
      </div>
    );
  }

  if (!orderInfo) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '70vh',
        flexDirection: 'column'
      }}>
        <Result
          status="warning"
          title="No se encontró información del pedido"
          subTitle="La información de tu compra no está disponible"
          extra={[
            <Button type="primary" key="home" onClick={() => navigate("/marketplace")}>
              Volver al Marketplace
            </Button>
          ]}
        />
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 1000,
      margin: "0 auto",
      padding: "20px",
      minHeight: "100vh",
      backgroundColor: "#f8f9fa"
    }}>
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          border: "none"
        }}
      >
        <Result
          icon={<CheckCircleOutlined style={{
            color: "#52c41a",
            fontSize: "80px",
            marginBottom: 20
          }} />}
          title={
            <Title level={2} style={{ color: "#1890ff", marginBottom: 10 }}>
              ¡Gracias por tu compra, {orderInfo.customer_name}!
            </Title>
          }
          subTitle={
            <Paragraph type="secondary" style={{ fontSize: "16px" }}>
              Tu pedido ha sido confirmado y está siendo procesado.
              Hemos enviado los detalles a <strong>{orderInfo.customer_email}</strong>
            </Paragraph>
          }
        />

        <div style={{ margin: "40px 0" }}>
          <Steps current={1} labelPlacement="vertical">
            <Step
              title="Pedido confirmado"
              description="Hoy"
              icon={<CheckCircleOutlined />}
            />
            <Step
              title="Preparando envío"
              description="En 24 horas"
              icon={<TruckOutlined />}
            />
            <Step
              title="En camino"
              description={`${orderInfo.estimated_delivery}`}
              icon={<TruckOutlined />}
            />
            <Step
              title="Entregado"
              description="Próximamente"
              icon={<UserOutlined />}
            />
          </Steps>
        </div>

        <Divider />

        <div style={{ marginBottom: 40 }}>
          <Title level={4}>Resumen de tu pedido</Title>
          <Card
            bordered={false}
            style={{
              backgroundColor: "#f6ffed",
              marginTop: 16
            }}
          >
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Número de pedido">
                <Text strong copyable>{orderInfo.order_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de compra">
                <Text>{orderInfo.date}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                {/* CORREGIDO: Usar formatPrice en lugar de toFixed directamente */}
                <Text type="success" strong>
                  ${formatPrice(orderInfo.total)} MXN
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Cantidad de productos">
                <Text strong>{orderInfo.items}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Fecha estimada de entrega">
                <Text type="warning">{orderInfo.estimated_delivery}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                <Text style={{ color: "#52c41a" }}>Confirmado</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {renderProductSummary()}
        </div>
        
        <div style={{ marginBottom: 40 }}>
          <Title level={4}>Información de envío</Title>
          <Card>
            <Paragraph>
              <MailOutlined /> <strong>Email de confirmación:</strong> {orderInfo.customer_email}
            </Paragraph>
            <Paragraph>
              <UserOutlined /> <strong>Dirección de envío:</strong> {orderInfo.shipping_address}
            </Paragraph>
            <Paragraph type="secondary">
              Recibirás una notificación por correo cuando tu pedido sea enviado.
            </Paragraph>
          </Card>
        </div>
        
        <div style={{
          marginBottom: 40,
          padding: 24,
          backgroundColor: "#e6f7ff",
          borderRadius: 8
        }}>
          <Title level={4}>📱 ¿Qué puedes hacer ahora?</Title>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 20,
            marginTop: 20
          }}>
            <Card hoverable style={{ textAlign: "center" }}>
              <FileTextOutlined style={{ fontSize: 32, color: "#1890ff" }} />
              <Paragraph strong>Ver mi pedido</Paragraph>
              <Paragraph type="secondary">Revisa el estado y detalles</Paragraph>
            </Card>
            <Card hoverable style={{ textAlign: "center" }}>
              <ShoppingOutlined style={{ fontSize: 32, color: "#52c41a" }} />
              <Paragraph strong>Seguir comprando</Paragraph>
              <Paragraph type="secondary">Explora más productos</Paragraph>
            </Card>
            <Card hoverable style={{ textAlign: "center" }}>
              <HomeOutlined style={{ fontSize: 32, color: "#722ed1" }} />
              <Paragraph strong>Ir al inicio</Paragraph>
              <Paragraph type="secondary">Volver al marketplace</Paragraph>
            </Card>
          </div>
        </div>

        {/* Botones de acción */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: 16,
          flexWrap: "wrap",
          marginTop: 40
        }}>
          <Button
            type="primary"
            size="large"
            icon={<ShoppingOutlined />}
            onClick={() => navigate("/marketplace")}
            style={{
              padding: "0 40px",
              height: 50,
              fontSize: 16
            }}
          >
            Seguir Comprando
          </Button>

          <Button
            size="large"
            icon={<FileTextOutlined />}
            onClick={() => navigate("/marketplace/orders")}
            style={{
              padding: "0 40px",
              height: 50,
              fontSize: 16
            }}
          >
            Ver Mis Pedidos
          </Button>

          <Button
            size="large"
            icon={<HomeOutlined />}
            onClick={() => navigate("/")}
            style={{
              padding: "0 40px",
              height: 50,
              fontSize: 16
            }}
          >
            Ir al Inicio
          </Button>
        </div>

        {/* Redirección automática */}
        <div style={{
          textAlign: "center",
          marginTop: 30,
          padding: 16,
          backgroundColor: "#fff7e6",
          borderRadius: 8
        }}>
          <Paragraph type="secondary">
            Serás redirigido automáticamente al marketplace en{" "}
            <Text strong style={{ color: "#fa8c16" }}>{countdown}</Text>{" "}
            segundos...
          </Paragraph>
          <Button type="link" onClick={() => navigate("/marketplace")}>
            Redirigir ahora
          </Button>
        </div>

        {/* Información adicional */}
        <Divider />
        <div style={{ textAlign: "center" }}>
          <Paragraph type="secondary">
            <small>
              Si tienes alguna pregunta sobre tu pedido, contacta a nuestro equipo de soporte
              en <a href="mailto:soporte@marketplace.com">soporte@epno.com.mx</a> o llama al
              656-703-8586.
            </small>
          </Paragraph>
          <Paragraph type="secondary">
            <small>
              Horario de atención: Lunes a Viernes de 8:30 AM a 5:00 PM
            </small>
          </Paragraph>
        </div>
      </Card>
    </div>
  );
};

export default ThankYouPage;